'use client';

/**
 * MaskedInput Component
 *
 * A text input with masking support for SSN, EIN, and phone numbers.
 * Features visibility toggle for sensitive fields.
 *
 * @see Issue #432 - Templated Loan Intake Wizard
 */

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha, styled, useTheme } from '@mui/material/styles';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import { motion } from 'framer-motion';
import type React from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { baseColors } from '../../theme/palette';
import { focusRings } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export type MaskType = 'ssn' | 'ein' | 'phone' | 'credit-card';

export interface MaskedInputProps extends Omit<TextFieldProps, 'variant' | 'value' | 'onChange'> {
  /**
   * Type of mask to apply
   */
  mask: MaskType;

  /**
   * Current value (unmasked digits only)
   */
  value: string;

  /**
   * Change handler (receives unmasked digits only)
   */
  onChange: (value: string) => void;

  /**
   * Glow color on focus
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary';

  /**
   * Show visibility toggle for sensitive fields
   * @default true for SSN, false for others
   */
  showToggle?: boolean;

  /**
   * Initial visibility state
   * @default false (masked)
   */
  initialVisible?: boolean;
}

// ============================================================================
// Mask Configurations
// ============================================================================

interface MaskConfig {
  pattern: RegExp;
  maxLength: number;
  format: (value: string, visible: boolean) => string;
  placeholder: string;
  ariaLabel: string;
}

const MASK_CONFIGS: Record<MaskType, MaskConfig> = {
  ssn: {
    pattern: /^\d{0,9}$/,
    maxLength: 9,
    format: (value: string, visible: boolean) => {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      if (digits.length === 0) return '';

      if (visible) {
        // Visible: XXX-XX-XXXX
        if (digits.length <= 3) return digits;
        if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      }
      // Masked: •••-••-XXXX (show last 4)
      if (digits.length <= 3) return '•'.repeat(digits.length);
      if (digits.length <= 5) return `•••-${'•'.repeat(digits.length - 3)}`;
      if (digits.length < 9) return `•••-••-${digits.slice(5)}`;
      return `•••-••-${digits.slice(5)}`;
    },
    placeholder: '___-__-____',
    ariaLabel: 'Social Security Number',
  },
  ein: {
    pattern: /^\d{0,9}$/,
    maxLength: 9,
    format: (value: string, visible: boolean) => {
      const digits = value.replace(/\D/g, '').slice(0, 9);
      if (digits.length === 0) return '';

      if (visible) {
        // Visible: XX-XXXXXXX
        if (digits.length <= 2) return digits;
        return `${digits.slice(0, 2)}-${digits.slice(2)}`;
      }
      // Masked: ••-•••••XX (show last 2)
      if (digits.length <= 2) return '•'.repeat(digits.length);
      if (digits.length < 9) return `••-${'•'.repeat(digits.length - 2)}`;
      return `••-•••••${digits.slice(7)}`;
    },
    placeholder: '__-_______',
    ariaLabel: 'Employer Identification Number',
  },
  phone: {
    pattern: /^\d{0,10}$/,
    maxLength: 10,
    format: (value: string) => {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      if (digits.length === 0) return '';

      // Format: (XXX) XXX-XXXX
      if (digits.length <= 3) return `(${digits}`;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    },
    placeholder: '(___) ___-____',
    ariaLabel: 'Phone Number',
  },
  'credit-card': {
    pattern: /^\d{0,16}$/,
    maxLength: 16,
    format: (value: string, visible: boolean) => {
      const digits = value.replace(/\D/g, '').slice(0, 16);
      if (digits.length === 0) return '';

      // Format: XXXX XXXX XXXX XXXX
      const groups = [];
      for (let i = 0; i < digits.length; i += 4) {
        groups.push(digits.slice(i, i + 4));
      }

      if (visible) {
        return groups.join(' ');
      }
      // Masked: •••• •••• •••• XXXX (show last 4)
      if (digits.length <= 4) return groups.join(' ');
      const maskedGroups = groups.map((g, i) => (i < groups.length - 1 ? '••••' : g));
      return maskedGroups.join(' ');
    },
    placeholder: '____ ____ ____ ____',
    ariaLabel: 'Credit Card Number',
  },
};

// ============================================================================
// Styled Components
// ============================================================================

interface StyledTextFieldProps {
  $isDark: boolean;
  $glowColor: 'primary' | 'secondary';
  $isFocused: boolean;
  $hasError: boolean;
}

const shouldForwardProp = (prop: string) => !prop.startsWith('$');

const StyledTextField = styled(TextField, { shouldForwardProp })<StyledTextFieldProps>(
  ({ theme, $isDark, $glowColor, $isFocused, $hasError }) => {
    // Use theme colors for primary/secondary to respect theme overrides
    const primaryColor = theme.palette.primary.main;
    const secondaryColor = theme.palette.secondary.main;
    const focusColor = $glowColor === 'primary' ? primaryColor : secondaryColor;
    const errorColor = theme.palette.error.main;

    let borderColor = $isDark ? 'rgba(255, 255, 255, 0.15)' : baseColors.neutral[300];
    let glowShadow = 'none';

    if ($hasError) {
      borderColor = errorColor;
      glowShadow = focusRings.error;
    } else if ($isFocused) {
      borderColor = focusColor;
      glowShadow = `0 0 0 3px ${alpha(focusColor, 0.25)}`;
    }

    return {
      '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
        transition: transitionPresets.input,
        fontFamily: 'monospace',
        letterSpacing: '0.1em',

        '& fieldset': {
          borderColor,
          borderWidth: $isFocused || $hasError ? 2 : 1,
          transition: transitionPresets.input,
        },

        '&:hover fieldset': {
          borderColor: $hasError ? errorColor : $isDark ? 'rgba(255, 255, 255, 0.3)' : alpha(primaryColor, 0.5),
        },

        '&.Mui-focused': {
          backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.01)',

          '& fieldset': {
            borderColor: $hasError ? errorColor : focusColor,
            boxShadow: glowShadow,
          },
        },

        '& input': {
          padding: '11.5px 16px',
          fontSize: '0.875rem',
          letterSpacing: '0.15em',
        },
      },

      '& .MuiInputLabel-root': {
        color: $isDark ? 'rgba(255, 255, 255, 0.6)' : baseColors.neutral[500],
        fontSize: '0.875rem',
        transition: transitionPresets.allFast,

        // Add background when label is shrunk (floating on border)
        '&.MuiInputLabel-shrink': {
          backgroundColor: $isDark ? '#1a1a1a' : '#ffffff',
          padding: '0 6px',
          marginLeft: '-4px',
        },

        '&.Mui-focused': {
          color: $hasError ? errorColor : focusColor,
        },

        '&.Mui-error': {
          color: errorColor,
        },
      },

      '& .MuiFormHelperText-root': {
        marginLeft: 4,
        marginTop: 6,
      },
    };
  },
);

const GlowWrapper = styled(motion.div)({
  position: 'relative',
  width: '100%',
});

const ToggleButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.5)' : baseColors.neutral[400],
  transition: transitionPresets.allFast,
  '&:hover': {
    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.8)' : baseColors.neutral[600],
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));

// ============================================================================
// Component
// ============================================================================

export const MaskedInput = forwardRef<HTMLInputElement, MaskedInputProps>(
  (
    {
      mask,
      value,
      onChange,
      glowColor = 'primary',
      showToggle,
      initialVisible = false,
      error,
      onFocus,
      onBlur,
      placeholder,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [isFocused, setIsFocused] = useState(false);
    const [isVisible, setIsVisible] = useState(initialVisible);

    const config = MASK_CONFIGS[mask];

    // Default showToggle to true for sensitive masks
    const shouldShowToggle = showToggle ?? (mask === 'ssn' || mask === 'ein' || mask === 'credit-card');

    // Format the display value
    const displayValue = useMemo(() => {
      return config.format(value || '', isVisible);
    }, [value, isVisible, config]);

    // Handle input change - extract digits and validate
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Extract only digits
        const digits = inputValue.replace(/\D/g, '').slice(0, config.maxLength);

        // Only update if valid
        if (config.pattern.test(digits)) {
          onChange(digits);
        }
      },
      [onChange, config],
    );

    // Handle key press to allow navigation and deletion
    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, arrow keys
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (allowedKeys.includes(e.key)) {
        return;
      }

      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      if (e.ctrlKey || e.metaKey) {
        return;
      }

      // Block non-numeric keys
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    }, []);

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        onFocus?.(e);
      },
      [onFocus],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        onBlur?.(e);
      },
      [onBlur],
    );

    const toggleVisibility = useCallback(() => {
      setIsVisible((prev) => !prev);
    }, []);

    return (
      <GlowWrapper
        animate={{
          scale: isFocused ? 1.005 : 1,
        }}
        transition={{ duration: 0.15, ease: [0.215, 0.61, 0.355, 1] }}
      >
        <StyledTextField
          ref={ref}
          $isDark={isDark}
          $glowColor={glowColor}
          $isFocused={isFocused}
          $hasError={Boolean(error)}
          error={Boolean(error)}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          variant="outlined"
          fullWidth
          placeholder={placeholder || config.placeholder}
          slotProps={{
            htmlInput: {
              'aria-label': config.ariaLabel,
              inputMode: 'numeric' as const,
              autoComplete: mask === 'ssn' ? 'off' : undefined,
              maxLength: config.maxLength + 10, // Account for formatting characters
            },
            input: {
              endAdornment: shouldShowToggle ? (
                <InputAdornment position="end">
                  <ToggleButton
                    aria-label={isVisible ? 'Hide value' : 'Show value'}
                    onClick={toggleVisibility}
                    edge="end"
                    size="small"
                  >
                    {isVisible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </ToggleButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
          {...props}
        />
      </GlowWrapper>
    );
  },
);

MaskedInput.displayName = 'MaskedInput';

export default MaskedInput;
